import React, { useEffect, useMemo, useState } from 'react';
import { ExternalLink, Loader2, Search, Filter as FilterIcon } from 'lucide-react';
import ThumbIcon from 'shared/components/ui/ThumbIcon';
import BrandDropdown from 'shared/components/ui/BrandDropdown';
import TableFooter from 'shared/components/tables/TableFooter';
import { listDisagreements, ruleDisagreement } from 'features/evals3/api/evals3Api';
import BrandLoader, { PageLoader } from 'shared/components/ui/BrandLoader';
import SidePanel from 'shared/components/ui/SidePanel';
import { activityToolbarButtonClass, tableHeaderRowClass, tableHeaderCellClass, tableRowClass, tableDataCellClass, activitySearchWrapperClass, activitySearchInputClass } from 'features/activity/styles/dataTableStyles';

/**
 * Spot checks, for a person to rule on (TRO-440).
 *
 * An eval saves money by stopping early: a cheap step decides the obvious
 * cases so the expensive judge never sees them. That is a bet, and nothing
 * about a green dashboard tells you whether the bet is paying — a step that
 * wrongly passes things is invisible precisely because it stopped the walk.
 *
 * So a small, fixed slice of traffic ignores the shortcut and runs the whole
 * eval anyway. The cheap step still records what it WOULD have decided, and
 * the two answers are compared. They almost always agree; the ones that do
 * not come here, and a person says which side was right. That ruling is what
 * turns "the step disagreed" into "the step was wrong", which is the number
 * the screen-error rate is made of and the evidence the pattern detector
 * reads.
 *
 * People only ever see the disagreements, never the whole slice: agreement is
 * not worth anyone's afternoon.
 *
 * The same shape as the grading queue beside it — one line per item, ruled
 * where it sits, opened beside the list for the whole path — because they are
 * two tabs of one page and a reviewer should not learn two layouts. What is
 * deliberately NOT the same: no selection. "Which side was right" is the one
 * question in this product that cannot be answered for twenty rows at once.
 */

const th = tableHeaderCellClass;
const td = tableDataCellClass;
const PAGE_LIMIT = 50;
const verdictWord = (passed) => (passed ? 'PASS' : 'FAIL');

function ago(iso) {
  if (!iso) return '';
  const sec = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (sec < 60) return `${sec}s`;
  const min = Math.floor(sec / 60); if (min < 60) return `${min}m`;
  const hr = Math.floor(min / 60); if (hr < 24) return `${hr}h`;
  return `${Math.floor(hr / 24)}d`;
}

function valueText(output) {
  const v = output?.effective_value ?? output?.value;
  if (v === null || v === undefined) return '';
  if (typeof v !== 'object') return String(v);
  return Object.entries(v).map(([k, x]) => `${k} ${typeof x === 'number' ? Number(x.toFixed(3)) : String(x)}`).join(', ');
}

function Block({ label, children }) {
  return (
    <div>
      <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-[color:var(--text-muted)]">{label}</div>
      {children}
    </div>
  );
}

const RULE_BTN = 'inline-flex h-6 items-center gap-1 whitespace-nowrap rounded-md border border-[color:var(--border-default)] px-2 text-[11.5px] font-medium text-[color:var(--text-secondary)] transition-colors hover:bg-surface-hover disabled:opacity-40';

/** Which side was right, for one row. */
function RuleButtons({ busy, onRule, labelFor }) {
  return (
    <span className="flex items-center justify-end gap-1">
      <button type="button" disabled={Boolean(busy)} onClick={(e) => { e.stopPropagation(); onRule('screen'); }}
        aria-label={labelFor('the cheap step')} title="The cheap step was right — stopping early here cost nothing"
        className={`${RULE_BTN} hover:border-[color:var(--feedback-success)] hover:text-success`}>
        {busy === 'screen' ? <Loader2 className="h-3 w-3 animate-spin" aria-hidden /> : <ThumbIcon direction="up" size={12} />}
        cheap step
      </button>
      <button type="button" disabled={Boolean(busy)} onClick={(e) => { e.stopPropagation(); onRule('full'); }}
        aria-label={labelFor('the whole eval')} title="The whole eval was right — the cheap step would have missed this"
        className={`${RULE_BTN} hover:border-[color:var(--feedback-error)] hover:text-danger`}>
        {busy === 'full' ? <Loader2 className="h-3 w-3 animate-spin" aria-hidden /> : <ThumbIcon direction="down" size={12} />}
        whole eval
      </button>
    </span>
  );
}

export default function AuditDisagreementsView({ teamId, lineageId = null, onOpen }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [query, setQuery] = useState('');
  const [busy, setBusy] = useState(null);      // { id, side }
  const [notice, setNotice] = useState(null);
  const [ruled, setRuled] = useState(0);
  const [total, setTotal] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [step, setStep] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.resolve(listDisagreements(teamId, { status: 'open', lineageId, limit: PAGE_LIMIT }))
      .then((rows) => {
        if (cancelled) return;
        setItems(rows || []);
        setTotal(rows?.total ?? null);
        setError(null);
      })
      .catch((e) => { if (!cancelled) setError(e?.response?.data?.error || e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [teamId, lineageId]);

  const loadMore = async () => {
    if (loadingMore) return;
    setLoadingMore(true);
    try {
      const more = await listDisagreements(teamId, { status: 'open', lineageId, limit: PAGE_LIMIT, offset: items.length });
      setItems((cur) => { const seen = new Set(cur.map((i) => i.id)); return [...cur, ...more.filter((i) => !seen.has(i.id))]; });
      setTotal(more.total ?? null);
    } catch (e) { setError(e?.response?.data?.error || e.message); }
    finally { setLoadingMore(false); }
  };

  const steps = useMemo(() => [...new Set(items.map((i) => i.shadow_verdict?.shadow?.node_id).filter(Boolean))].sort(), [items]);
  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter((it) => (!step || it.shadow_verdict?.shadow?.node_id === step)
      && (!q || `${it.lineage_name || ''} ${it.shadow_verdict?.shadow?.node_id || ''}`.toLowerCase().includes(q)));
  }, [items, query, step]);

  const item = items.find((i) => i.id === openId) || null;
  const showEvalColumn = !lineageId;

  const leave = (id) => {
    const i = items.findIndex((x) => x.id === id);
    const next = items.filter((x) => x.id !== id);
    setItems(next);
    setTotal((t) => (t == null ? t : Math.max(0, t - 1)));
    if (openId === id) setOpenId(next.length ? next[Math.min(Math.max(i, 0), next.length - 1)].id : null);
  };

  const rule = async (id, right) => {
    if (busy) return;
    setBusy({ id, side: right });
    setNotice(null);
    try {
      const r = await ruleDisagreement(teamId, id, right);
      if (r && r.already_ruled) {
        setNotice({ id, text: `Someone already ruled on this one: the ${r.item.graded_choice === 'screen' ? 'cheap step' : 'whole eval'} was right.` });
      } else {
        setRuled((k) => k + 1);
        leave(id);
      }
    } catch (e) {
      setNotice({ id, text: e?.response?.data?.error || e.message, error: true });
    } finally { setBusy(null); }
  };

  const sv = item?.shadow_verdict || {};
  const target = item && (item.target_kind === 'run' ? { kind: 'run', id: item.run_id } : item.target_kind === 'span' ? { kind: 'span', id: item.span_id } : { kind: 'conversation', id: item.conversation_id });

  return (
    <>
      <div className="min-h-0 flex-1 overflow-y-auto" aria-label="Audit disagreements">
        <div className="flex flex-col gap-2 bg-surface px-3 pb-2 pt-3 sm:px-4">
          <div className="flex flex-wrap items-center gap-2">
            <label className={`${activitySearchWrapperClass} w-56`}>
              <Search className="h-3.5 w-3.5 flex-none text-[color:var(--text-muted)]" aria-hidden />
              <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Filter spot checks" aria-label="Filter spot checks"
                className={activitySearchInputClass} />
            </label>
            <div className="relative">
              <button type="button" onClick={() => setFilterOpen((v) => !v)} aria-haspopup="dialog" aria-expanded={filterOpen} className={activityToolbarButtonClass}>
                <FilterIcon size={13} aria-hidden /> Filter
                {step && <span className="rounded-full bg-brand-soft px-1.5 text-[10.5px] font-semibold text-brand">1</span>}
              </button>
              {filterOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setFilterOpen(false)} role="presentation" />
                  <div role="dialog" aria-label="Filter spot checks" className="absolute left-0 top-full z-50 mt-1 flex w-64 flex-col gap-1 rounded-md border border-[color:var(--border-default)] bg-[color:var(--surface-raised)] p-3 shadow-token-lg">
                    <span className="text-[10.5px] font-semibold uppercase tracking-wide text-[color:var(--text-muted)]">Cheap step</span>
                    <BrandDropdown compact ariaLabel="Cheap step" value={step} onChange={setStep} options={[{ value: '', label: 'Any' }, ...steps.map((x) => ({ value: x, label: x }))]} />
                    {step && <button type="button" onClick={() => setStep('')} className="mt-1 self-start text-[11.5px] text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)]">Clear</button>}
                  </div>
                </>
              )}
            </div>
            <div className="flex-1" />
            {ruled > 0 || loading ? (
              <span className="text-[11.5px] tabular-nums text-[color:var(--text-muted)]" role="status">
                {ruled ? `${ruled} ruled just now` : ''}{loading ? `${ruled ? ' · ' : ''}loading…` : ''}
              </span>
            ) : null}
          </div>
        </div>

        {notice && notice.id !== openId && (
          <div role={notice.error ? 'alert' : 'status'} data-testid="audit-notice" className={`flex items-center gap-2 border-b border-[color:var(--border-subtle)] px-4 py-1.5 text-[12px] ${notice.error ? 'text-danger' : 'text-[color:var(--text-secondary)]'}`}>
            {notice.text}
            <button type="button" onClick={() => setNotice(null)} className="ml-auto rounded px-1.5 py-0.5 text-[11px] text-[color:var(--text-muted)] hover:bg-surface-hover">Dismiss</button>
          </div>
        )}
        {error && <div role="alert" className="mx-4 mt-3 rounded-md border border-[color:var(--feedback-error)] bg-danger-soft p-2 text-[12px] text-danger">{error}</div>}
        {loading && !items.length && <div className="flex justify-center py-12"><BrandLoader label="Loading spot checks" /></div>}

        {!loading && !items.length && !error && (
          <div className="mx-4 mb-4 mt-3 flex h-40 items-center justify-center rounded-md border border-dashed border-[color:var(--border-default)] px-6 text-center text-[12px] text-[color:var(--text-muted)]">
            {ruled > 0 ? `All caught up — ${ruled} ruled.` : 'Nothing to rule on.'}
          </div>
        )}

        {items.length > 0 && (
          <div className="mx-4 mb-4 mt-3 overflow-hidden rounded-md border border-[color:var(--border-default)] bg-surface shadow-token-sm">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[640px] table-fixed text-left">
                <caption className="sr-only">Spot checks waiting for a ruling</caption>
                <thead>
                  <tr className={tableHeaderRowClass}>
                    <th scope="col" className={`${th} w-[26%]`}>The cheap step</th>
                    <th scope="col" className={th}>It would have said</th>
                    <th scope="col" className={th}>The whole eval said</th>
                    {showEvalColumn && <th scope="col" className={`${th} w-[130px]`}>Eval</th>}
                    <th scope="col" className={`${th} w-[56px] text-right`}>Waited</th>
                    <th scope="col" className={`${th} sticky right-0 bg-surface text-right shadow-[-8px_0_8px_-8px_rgba(0,0,0,0.25)] w-[190px]`}>Which was right?</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((it) => {
                    const isOpen = it.id === openId;
                    const s = it.shadow_verdict || {};
                    const step = s.shadow?.node_id || 'a step';
                    return (
                      <tr key={it.id} onClick={() => setOpenId(it.id)} tabIndex={0} aria-selected={isOpen}
                        onKeyDown={(e) => { if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); setOpenId(it.id); } }}
                        aria-label={`${step}, waiting ${ago(it.created_at)}`}
                        className={`group ${tableRowClass} ${isOpen ? 'bg-surface-hover' : ''} focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[color:var(--state-focus-ring)]`}>
                        <td className={`${td} truncate font-medium text-[color:var(--text-primary)]`} title={step}>{step}</td>
                        <td className={`${td} truncate text-[color:var(--text-secondary)]`}>
                          {verdictWord(s.shadow?.passed)}{s.shadow?.reason_code ? ` (${s.shadow.reason_code})` : ''}
                        </td>
                        <td className={`${td} truncate text-[color:var(--text-secondary)]`}>
                          {verdictWord(s.full?.passed)}{s.full?.reason_code ? ` (${s.full.reason_code})` : ''}
                        </td>
                        {showEvalColumn && <td className={`${td} truncate text-[color:var(--text-secondary)]`} title={it.lineage_name}>{it.lineage_name}</td>}
                        <td className={`${td} whitespace-nowrap text-right text-[color:var(--text-muted)]`} title={it.created_at}>{ago(it.created_at)}</td>
                        <td className={`${td} sticky right-0 text-right shadow-[-8px_0_8px_-8px_rgba(0,0,0,0.25)] ${isOpen ? 'bg-surface-hover' : 'bg-surface group-hover:bg-surface-hover'}`} onClick={(e) => e.stopPropagation()}>
                          <RuleButtons busy={busy?.id === it.id ? busy.side : null} onRule={(side) => rule(it.id, side)}
                            labelFor={(who) => `On ${step}, ${who} was right`} />
                        </td>
                      </tr>
                    );
                  })}
                  {visible.length === 0 && (
                    <tr><td colSpan={showEvalColumn ? 6 : 5} className={`${td} py-6 text-center text-[color:var(--text-muted)]`}>Nothing here matches “{query}”.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <TableFooter
              shown={visible.length}
              total={step || query ? null : total}
              hasMore={total != null && items.length < total}
              loading={loadingMore}
              onLoadMore={loadMore}
              note={step || query ? `${visible.length.toLocaleString()} of ${items.length.toLocaleString()} loaded match` : null}
              className="border-t border-[color:var(--border-default)] px-3 pb-2"
            />
          </div>
        )}
      </div>

      <SidePanel
        open={Boolean(openId)}
        onClose={() => setOpenId(null)}
        ariaLabel="Spot check"
        storageKey="trodo.evals3.spotCheckPanel.width"
        defaultWidth={520}
        title={(
          <span className="flex items-baseline gap-1.5">
            <span className="truncate">{item?.lineage_name || 'Spot check'}</span>
            {item?.version != null && <span className="shrink-0 text-[11px] font-normal text-[color:var(--text-muted)]">v{item.version}</span>}
          </span>
        )}
        actions={target && target.id ? (
          <button type="button" onClick={() => onOpen && onOpen(target)} className={activityToolbarButtonClass}>
            <ExternalLink className="h-3 w-3" aria-hidden /> Open the full {item.target_kind}
          </button>
        ) : null}
      >
        <div className="min-h-0 flex-1 px-4 py-4">
          {!item ? <PageLoader label="Loading this spot check" /> : (
            <div className="flex flex-col gap-4">
              <div className="grid grid-cols-1 gap-2">
                <div className="rounded-md border border-[color:var(--border-default)] p-3" data-testid="audit-screen-side">
                  <div className="text-[10px] font-semibold uppercase tracking-wide text-[color:var(--text-muted)]">The cheap step</div>
                  <div className="mt-1 text-[13px] text-[color:var(--text-primary)]">
                    <span className="font-mono">{sv.shadow?.node_id}</span> would have said <b>{verdictWord(sv.shadow?.passed)}</b>
                    {sv.shadow?.reason_code ? ` (${sv.shadow.reason_code})` : ''}
                  </div>
                </div>
                <div className="rounded-md border border-[color:var(--border-default)] p-3" data-testid="audit-full-side">
                  <div className="text-[10px] font-semibold uppercase tracking-wide text-[color:var(--text-muted)]">The whole eval</div>
                  <div className="mt-1 text-[13px] text-[color:var(--text-primary)]">
                    said <b>{verdictWord(sv.full?.passed)}</b>{sv.full?.reason_code ? ` (${sv.full.reason_code})` : ''} at <span className="font-mono">{sv.full?.node_id}</span>
                  </div>
                </div>
              </div>

              <Block label="Every step it walked">
                <ol className="flex flex-col gap-1" data-testid="audit-steps">
                  {(item.steps || []).map((s) => (
                    <li key={s.seq} className="rounded border border-[color:var(--border-subtle)] bg-[color:var(--surface-sunken)] px-2 py-1 text-[11.5px]">
                      <span className="mr-1.5 font-mono text-[10px] text-[color:var(--text-muted)]">{s.seq}</span>
                      <span className="font-medium text-[color:var(--text-primary)]">{s.node_id}</span>
                      <span className="ml-1.5 text-[color:var(--text-muted)]">{s.instrument}</span>
                      {s.shadow && <span className="ml-1.5 rounded bg-warning-soft px-1 text-[10px] text-warning">shadowed</span>}
                      <span className="ml-2 text-[color:var(--text-secondary)]">{valueText(s.output)}</span>
                      {s.shadow && s.output?.audit?.would_take && <span className="ml-2 text-[color:var(--text-muted)]">would have gone to {s.output.audit.would_take}</span>}
                      {s.edge_taken && <span className="ml-2 text-[color:var(--text-muted)]">→ {s.edge_taken}</span>}
                    </li>
                  ))}
                </ol>
              </Block>

              <Block label="Which was right?">
                <div className="flex flex-wrap gap-2" role="group" aria-label="Ruling">
                  <button type="button" disabled={Boolean(busy)} onClick={() => rule(item.id, 'screen')} aria-label="The cheap step was right"
                    className="flex min-w-[160px] flex-col items-start rounded-lg border border-[color:var(--border-default)] px-3 py-2 text-left hover:bg-surface-hover hover:border-[color:var(--feedback-success)] disabled:opacity-50">
                    <span className="flex items-center gap-1.5 text-[13px] font-semibold text-[color:var(--text-primary)]">
                      {busy?.id === item.id && busy.side === 'screen' ? <Loader2 className="h-3 w-3 animate-spin" aria-hidden /> : <ThumbIcon direction="up" size={12} />}
                      The cheap step was right
                    </span>
                    <span className="text-[10.5px] text-[color:var(--text-muted)]">stopping early here cost nothing</span>
                  </button>
                  <button type="button" disabled={Boolean(busy)} onClick={() => rule(item.id, 'full')} aria-label="The whole eval was right"
                    className="flex min-w-[160px] flex-col items-start rounded-lg border border-[color:var(--border-default)] px-3 py-2 text-left hover:bg-surface-hover hover:border-[color:var(--feedback-error)] disabled:opacity-50">
                    <span className="flex items-center gap-1.5 text-[13px] font-semibold text-[color:var(--text-primary)]">
                      {busy?.id === item.id && busy.side === 'full' ? <Loader2 className="h-3 w-3 animate-spin" aria-hidden /> : <ThumbIcon direction="down" size={12} />}
                      The whole eval was right
                    </span>
                    <span className="text-[10.5px] text-[color:var(--text-muted)]">the cheap step would have missed this</span>
                  </button>
                </div>
              </Block>

              {notice && notice.id === item.id && (
                <div role="status" data-testid="audit-notice" className={`flex items-center gap-2 rounded-md border px-3 py-2 text-[12px] ${notice.error ? 'border-[color:var(--feedback-error)] text-danger' : 'border-[color:var(--border-default)] text-[color:var(--text-secondary)]'}`}>
                  {notice.text}
                  {!notice.error && (
                    <button type="button" onClick={() => leave(item.id)} className="ml-auto rounded border border-[color:var(--border-default)] px-2 py-0.5 text-[11px] hover:bg-surface-hover">Next</button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </SidePanel>
    </>
  );
}
